class BaseElement extends HTMLElement {
    static get props() {
        return {};
    }

    static get observedAttributes() {
        return Object.keys(this.props).map((propName) => {
            return this.attributeName(propName);
        });
    }

    static attributeName(propName) {
        return propName.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();
    }

    constructor() {
        super();

        // property is element id, value is the element
        // for any element with an id in the shadowroot
        this.$ = {};
        // two objects mapping property name to attribute name (and the reverse)
        this.attrToProp = {};
        this.propToAttr = {};
        Object.keys(this.constructor.props).forEach((name) => {
            const attrName = this.constructor.attributeName(name);
            this.attrToProp[attrName] = name;
            this.propToAttr[name] = attrName;
        });

        this.attachShadow({ mode: 'open' });

        this._renderPromise = null;
        this._isConnected = false;

        // regular reset object of props that changed
        // and triggered a rerender
        this._triggers = {};
        // storing prop name and values
        // seems to proxy them through getters/setters
        this._props = {};
    }

    connectedCallback() {
        if (this._isConnected) {
            return;
        }
        const initialProps = Object.keys(this.constructor.props).reduce(
            (all, propName) => {
                return {
                    ...all,
                    [propName]: this[propName]
                };
            },
            {}
        );

        Object.keys(this.constructor.props).forEach((propName) => {
            const propertyInfo = this.constructor.props[propName];
            const attrName = this.propToAttr[propName];

            let initialValue = this[propName];

            if (this.hasAttribute(attrName)) {
                initialValue = this.getAttribute(attrName);
                if ([Array, Object].includes(propertyInfo.type)) {
                    try {
                        initialValue = JSON.parse(initialValue);
                    } catch (err) {
                        // ignore
                    }
                }
            } else if (initialProps[propName] !== undefined) {
                initialValue = initialProps[propName];
            }

            Object.defineProperty(this, propName, {
                set: this.setter(propName),
                get: this.getter(propName)
            });

            this[propName] = initialValue;
        });

        this._isConnected = true;

        this._render(true, true);
    }

    disconnectedCallback() {
        this._isConnected = false;
    }

    _requestRender() {
        if (!this._renderPromise) {
            this._renderPromise = Promise.resolve().then(() => {
                this._render();
            });
        }
    }

    _render(force = false, first = false) {
        if (
            !force &&
            !Object.keys(this._triggers).length
        ) {
            return;
        }

        this._renderPromise = Promise.resolve().then(() => {
            if (first) {
                this.firstRender(this.shadowRoot, this._triggers);

                this.$ = [...this.shadowRoot.querySelectorAll('[id]')].reduce((all, el) => {
                    return {
                        ...all,
                        [el.id]: el
                    };
                }, {});

                this.firstRendered(this.shadowRoot, this._triggers);
            }

            this.render(this.shadowRoot, this._triggers);
            this.rendered(this.shadowRoot, this._triggers);

            this._renderPromise = null;
            this._triggers = {};
        });
    }

    forceRender() {
        this._render(true);
    }

    firstRender() {}

    render() {}

    firstRendered() {}

    rendered() {}

    setter(propName) {
        const attrName = this.propToAttr[propName];

        return (value) => {
            if (this._props[propName] === value) return;

            if (!(propName in this._triggers)) {
                this._triggers[propName] = this._props[propName];
            }

            this._props[propName] = value;

            const propertyInfo = this.constructor.props[propName];

            if (this._isConnected && this.constructor.observedAttributes.includes(attrName)) {
                let attributeValue = value;

                if (propertyInfo.type === Boolean) {
                    attributeValue = attributeValue || attributeValue === '';
                    if (attributeValue) {
                        this.setAttribute(attrName, '');
                    } else {
                        this.removeAttribute(attrName);
                    }
                } else {
                    if ([Array, Object].includes(propertyInfo.type)) {
                        attributeValue = JSON.stringify(attributeValue);
                    }

                    if (this.getAttribute(attrName) !== attributeValue) {
                        if ([null, undefined].includes(attributeValue)) {
                            this.removeAttribute(attrName);
                        } else {
                            this.setAttribute(attrName, attributeValue);
                        }
                    }
                }
            }

            if (this._isConnected) {
                this._requestRender();
            }
        };
    }

    getter(propName) {
        return () => this._props[propName];
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        const propName = this.attrToProp[attrName];

        const propertyInfo = this.constructor.props[propName];

        let propertyValue = newValue;

        switch (propertyInfo.type) {
            case Boolean:
                propertyValue = propertyValue === '';
                break;
            case Number:
                propertyValue = Number(propertyValue);
                break;
            case Array:
            case Object:
                try {
                    propertyValue = JSON.parse(propertyValue);
                } catch (err) {
                    // ignore
                }
                break;
            case String:
                break;
        }

        if (this[propName] !== propertyValue) {
            this[propName] = propertyValue;
        }
    }
}

export default BaseElement;
